import React, { useState } from 'react';
import { BackArrow } from '../../../../../components/BackArrow';
import { PageTitle } from '../../../../../components/PageTitle';
import { OfferRequisition } from '../../../../../entities/v1/applicant_tracking/OfferRequisition';
import { User } from '../../../../../entities/User';
import { Alert, AlertObject } from '../../../../../components/Alert';
import { StatusChip } from '../../../../../components/ApplicantTracking/Requisition/StatusChip';
import {
  buildSteps,
  getApprovableStepOrders,
  getCurrentStepOrder,
} from '../../../../../utils/applicant_tracking/requisition';
import { DeclinedAction } from './Actions/DeclinedAction';
import { ApproverActions } from './Actions/ApproverActions';
import { OwnerOrAdminActions } from './Actions/OwnerOrAdminActions';

interface PropTypes {
  currentUser: User;
  offerRequisition: OfferRequisition;
  onEditOfferRequisition: () => void;
  setLoadingState: (loading: boolean) => void;
  reloadOfferRequisition: () => void;
}

function isUserAbleToEdit(props: PropTypes): boolean {
  return (
    props.currentUser.isAdmin ||
    props.currentUser.id === props.offerRequisition.ownerUserId
  );
}

function isApprover(props: PropTypes): boolean {
  const approvalSteps = buildSteps(props.offerRequisition.approvers);
  const currentStepOrder = getCurrentStepOrder(approvalSteps);

  const approvableStepOrders = getApprovableStepOrders(
    props.offerRequisition,
    props.currentUser,
  );

  if (approvableStepOrders.length === 0) {
    return false;
  }

  return isUserAbleToEdit(props)
    ? approvableStepOrders.includes(currentStepOrder)
    : true;
}

function HeaderActions(
  props: PropTypes & { setAlertMessage: (message: AlertObject) => void },
) {
  if (props.offerRequisition.status === 'declined') {
    return <DeclinedAction {...props} />;
  }

  if (isApprover(props)) {
    return (
      <ApproverActions {...props} setAlertMessage={props.setAlertMessage} />
    );
  }

  if (isUserAbleToEdit(props)) {
    return (
      <OwnerOrAdminActions {...props} setAlertMessage={props.setAlertMessage} />
    );
  }
}

export function Header(props: PropTypes) {
  const [alertMessage, setAlertMessage] = useState<AlertObject>(null);

  return (
    <>
      {alertMessage && (
        <Alert
          type={alertMessage.type}
          autoClearTimeout={4000}
          clearable={true}
          onClose={() => setAlertMessage(null)}
        >
          {alertMessage.message}
        </Alert>
      )}
      <div className='d-flex align-items-center mb-4 justify-content-between'>
        <div className='d-flex align-items-center'>
          <BackArrow />
          <PageTitle
            text={`${props.offerRequisition.signableOffer.application.candidate.name} - Requisition Summary`}
          />
          <StatusChip status={props.offerRequisition.status} />
        </div>
        <HeaderActions {...props} setAlertMessage={setAlertMessage} />
      </div>
    </>
  );
}
